/** The systemic-map modules' intro screens and view switch (#1094) — keep keys in sync across fi / en / sv */

export const moduleIntroFi = {
  start: 'Aloitetaan!',
  phasesLabel: 'Työpajan vaiheet',
  terms: {
    kipupiste: { term: 'Ongelma', definition: 'Konkreettinen havainto, ilmiö tai ongelma, jonka Kuution asiantuntijat ovat tunnistaneet.' },
    ankkuri: { term: 'Muutoksen este', definition: 'Asia, joka pitää ongelmaa paikallaan, nähtynä yhden linssin läpi.' },
    juurisyy: { term: 'Juurisyy', definition: 'Mekanismi, joka pitää muutoksen estettä yllä.' },
    vipupiste: { term: 'Vipupiste', definition: 'Suunta, johon jonkin asian pitää muuttua, jotta juurisyyt heikkenevät, ja mittari, josta muutoksen näkee.' },
    linssi: { term: 'Linssi', definition: 'Näkökulma (esim. talous, teknologia, arvot), jonka läpi ongelmaa katsotaan.' },
    silmukka: { term: 'Silmukka', definition: 'Vaikutusten kehä, joka vahvistaa tai tasaa itseään.' },
    ratkaisu: { term: 'Ratkaisu', definition: 'Ehdotus, joka tarttuu ongelmaan yhden tai useamman vipupisteen kautta.' },
    ennuste: { term: 'Ennuste', definition: 'Tekoälyn arvio siitä, miten ratkaisu liikuttaisi vipupisteiden mittareita.' },
    ristivaikutus: { term: 'Ristivaikutus', definition: 'Miten kaksi ratkaisua tukevat, kilpailevat tai riippuvat toisistaan.' },
    skenaario: { term: 'Skenaario', definition: 'Kuvaus maailmasta, jossa ratkaisujen kulku kuvitellaan.' },
    kohtalo: { term: 'Kohtalo', definition: 'Mitä ratkaisulle käy: toteutuu, viivästyy, heikkenee tai kaatuu.' },
  },
  modules: {
    kipupisteet: {
      label: 'Ongelmat',
      title: 'Minkä ongelman olet tunnistanut?',
      lead: 'Kirjoitatte ongelmia, joita kohtaatte tai olette tunnistaneet tässä aiheessa, yksi kerrallaan omin sanoin. Tekoäly auttaa tarkentamaan jokaisen konkreettiseksi ennen tallennusta. Ongelmat ovat kartan juuret: kaikki myöhempi rakentuu niiden varaan.',
    },
    ankkurit: {
      label: 'Muutoksen esteet',
      title: 'Mikä estää ongelman ratkeamisen?',
      lead: 'Valitkaa ongelma ja sitten linssi, esimerkiksi talous tai teknologia. Keskustelussa etsitte asiat, jotka pitävät ongelmaa paikallaan: ne ovat muutoksen esteitä. Niistä tekoäly johtaa myöhemmin juurisyyt.',
    },
    juurisyyt: {
      label: 'Juurisyyt',
      title: 'Miksi esteet pysyvät?',
      lead: 'Tekoäly on johtanut jokaiselle muutoksen esteelle juurisyyt: mekanismit, jotka pitävät sitä yllä. Saat pakat omien esteidesi juurisyistä ja muutamasta muiden esteestä. Hyväksy, hylkää tai tarkenna kortti kerrallaan, ja lisää puuttuvat. Jokaisesta juurisyystä seuraa vipupiste: suunta, johon asiaa pitää muuttaa.',
    },
    systemicMap: {
      label: 'Ongelmakartta',
      title: 'Miltä kokonaisuus näyttää?',
      lead: 'Tässä näette kartan kokonaisuutena: mitkä vipupisteet yhdistävät eri ongelmien juurisyitä ja missä vaikutukset kiertävät silmukoina. Vipupiste kertoo, mihin suuntaan asian pitää muuttua; kun usea juurisyy jakaa sen, siihen vaikuttamalla liikutatte niitä kaikkia. Vipupisteiden mittarit voi sitoa Tilastokeskuksen tilastoihin.',
    },
    ratkaisut: {
      label: 'Ratkaisut',
      title: 'Mihin vipupisteeseen tartutte?',
      lead: 'Valitse vipupiste: kartta näyttää juurisyyt, joita se kantaa, sekä niiden esteet ja ongelmat, ja keskustelu auttaa kirjoittamaan ratkaisun, joka liikuttaa sitä. Keskustelu nostaa esiin mahdolliset vaikutukset ja sivuvaikutukset. Analyysinäkymässä tekoäly arvioi, mitä kukin ratkaisu tekisi kaikille vipupisteille ja mihin ongelmiin se tarttuu.',
    },
    future: {
      label: 'Tulevaisuustestaus',
      title: 'Miten ratkaisut pärjäävät tulevaisuudessa?',
      lead: 'Ensin arvioitte, miten valitut ratkaisut vaikuttavat toisiinsa. Sitten tekoäly kuvittelee niille kulun jokaisessa skenaariossa: mikä toteutuu, mikä viivästyy ja miksi.',
    },
  },
  views: { label: 'Näkymä', work: 'Työskentely', map: 'Kartta' },
  panel: { show: 'Näytä: {{name}}', hide: 'Piilota: {{name}}', kipupisteet: 'Ongelmat' },
  lensChip: 'Linssi: {{name}}',
};

export const moduleIntroEn = {
  start: "Let's start!",
  phasesLabel: "The workshop's phases",
  terms: {
    kipupiste: { term: 'Problem', definition: 'A concrete observation, phenomenon or problem that the experts in the Kuutio have identified.' },
    ankkuri: { term: 'Obstacle to change', definition: 'Something that keeps a problem in place, seen through one lens.' },
    juurisyy: { term: 'Root cause', definition: 'The mechanism that holds an obstacle to change up.' },
    vipupiste: { term: 'Leverage point', definition: 'The direction something must change in for the root causes to weaken, and the metric that would show the change.' },
    linssi: { term: 'Lens', definition: 'A perspective (e.g. economy, technology, values) through which a problem is viewed.' },
    silmukka: { term: 'Loop', definition: 'A cycle of effects that reinforces or balances itself.' },
    ratkaisu: { term: 'Solution', definition: 'A proposal that takes on a problem through one or more leverage points.' },
    ennuste: { term: 'Projection', definition: "The AI's estimate of how a solution would move the leverage points' metrics." },
    ristivaikutus: { term: 'Interplay', definition: 'How two solutions support, compete with or depend on each other.' },
    skenaario: { term: 'Scenario', definition: 'A description of a world in which the solutions\' course is imagined.' },
    kohtalo: { term: 'Fate', definition: 'What becomes of a solution: realised, delayed, diminished or failed.' },
  },
  modules: {
    kipupisteet: {
      label: 'Problems',
      title: 'Which problem have you identified?',
      lead: 'You write the problems you face or have identified in this topic, one at a time in your own words. The AI helps sharpen each into something concrete before it is saved. The problems are the roots of the map: everything after is built on them.',
    },
    ankkurit: {
      label: 'Obstacles to change',
      title: 'What stops the problem from being solved?',
      lead: 'Choose a problem and then a lens, such as the economy or technology. In the conversation you look for what keeps the problem in place: those are the obstacles to change. From them the AI later derives the root causes.',
    },
    juurisyyt: {
      label: 'Root causes',
      title: 'Why do the obstacles hold?',
      lead: 'The AI has derived root causes for every obstacle to change: the mechanisms that hold it up. You get decks of the root causes of your own obstacles and of a few of others\'. Approve, reject or refine one card at a time, and add what is missing. Every root cause points to a leverage point: the direction something must change in.',
    },
    systemicMap: {
      label: 'Problem mapping',
      title: 'What does the whole look like?',
      lead: 'Here you see the map as a whole: which leverage points connect the root causes of different problems, and where effects run round in loops. A leverage point says which way something must change; when several root causes share it, moving it moves them all. Its metrics can be bound to Statistics Finland data.',
    },
    ratkaisut: {
      label: 'Solutions',
      title: 'Which leverage point do you take on?',
      lead: 'Choose a leverage point: the map shows the root causes it carries, with their obstacles and problems, and the conversation helps you write a solution that moves it. It brings up possible effects and side effects as you go. In the analysis view the AI estimates what each solution would do to every leverage point, and which problems it takes on.',
    },
    future: {
      label: 'Future testing',
      title: 'How do the solutions fare in the future?',
      lead: 'First you judge how the chosen solutions affect each other. Then the AI imagines their course in each scenario: what is realised, what is delayed, and why.',
    },
  },
  views: { label: 'View', work: 'Working', map: 'Map' },
  panel: { show: 'Show: {{name}}', hide: 'Hide: {{name}}', kipupisteet: 'Problems' },
  lensChip: 'Lens: {{name}}',
};

export const moduleIntroSv = {
  start: 'Vi börjar!',
  phasesLabel: 'Workshopens faser',
  terms: {
    kipupiste: { term: 'Problem', definition: 'En konkret iakttagelse, ett fenomen eller ett problem som experterna i Kuutio har identifierat.' },
    ankkuri: { term: 'Hinder för förändring', definition: 'Något som håller ett problem på plats, sett genom en lins.' },
    juurisyy: { term: 'Grundorsak', definition: 'Mekanismen som håller ett hinder för förändring uppe.' },
    vipupiste: { term: 'Hävstångspunkt', definition: 'Riktningen något måste förändras i för att grundorsakerna ska försvagas, och mätaren som skulle visa förändringen.' },
    linssi: { term: 'Lins', definition: 'Ett perspektiv (t.ex. ekonomi, teknik, värderingar) genom vilket problemet betraktas.' },
    silmukka: { term: 'Slinga', definition: 'En kedja av effekter som förstärker eller balanserar sig själv.' },
    ratkaisu: { term: 'Lösning', definition: 'Ett förslag som tar sig an ett problem genom en eller flera hävstångspunkter.' },
    ennuste: { term: 'Prognos', definition: 'AI:ns uppskattning av hur en lösning skulle flytta hävstångspunkternas mätare.' },
    ristivaikutus: { term: 'Korsverkan', definition: 'Hur två lösningar stöder, konkurrerar med eller är beroende av varandra.' },
    skenaario: { term: 'Scenario', definition: 'En beskrivning av en värld där lösningarnas förlopp föreställs.' },
    kohtalo: { term: 'Öde', definition: 'Vad som händer med en lösning: förverkligas, försenas, försvagas eller misslyckas.' },
  },
  modules: {
    kipupisteet: {
      label: 'Problem',
      title: 'Vilket problem har du identifierat?',
      lead: 'Ni skriver de problem ni möter eller har identifierat i det här ämnet, ett i taget med egna ord. AI:n hjälper att skärpa vart och ett till något konkret innan det sparas. Problemen är kartans rötter: allt som följer bygger på dem.',
    },
    ankkurit: {
      label: 'Hinder för förändring',
      title: 'Vad hindrar att problemet löses?',
      lead: 'Välj ett problem och sedan en lins, till exempel ekonomi eller teknik. I samtalet letar ni efter det som håller problemet på plats: det är hinder för förändring. Ur dem härleder AI:n senare grundorsakerna.',
    },
    juurisyyt: {
      label: 'Grundorsaker',
      title: 'Varför står hindren kvar?',
      lead: 'AI:n har härlett grundorsaker för varje hinder för förändring: mekanismerna som håller det uppe. Du får högar med grundorsakerna till dina egna hinder och till några av andras. Godkänn, avvisa eller förfina ett kort i taget, och lägg till det som saknas. Varje grundorsak pekar på en hävstångspunkt: riktningen något måste förändras i.',
    },
    systemicMap: {
      label: 'Problemkartläggning',
      title: 'Hur ser helheten ut?',
      lead: 'Här ser ni kartan som en helhet: vilka hävstångspunkter som förbinder olika problems grundorsaker och var effekterna går runt i slingor. En hävstångspunkt säger åt vilket håll något måste förändras; när flera grundorsaker delar den flyttar ni alla genom att flytta den. Dess mätare kan kopplas till Statistikcentralens statistik.',
    },
    ratkaisut: {
      label: 'Lösningar',
      title: 'Vilken hävstångspunkt tar ni er an?',
      lead: 'Välj en hävstångspunkt: kartan visar grundorsakerna den bär, med deras hinder och problem, och samtalet hjälper er att skriva en lösning som flyttar den. Samtalet lyfter fram möjliga effekter och bieffekter. I analysvyn uppskattar AI:n vad varje lösning skulle göra med alla hävstångspunkter och vilka problem den tar sig an.',
    },
    future: {
      label: 'Framtidstestning',
      title: 'Hur klarar sig lösningarna i framtiden?',
      lead: 'Först bedömer ni hur de valda lösningarna påverkar varandra. Sedan föreställer sig AI:n deras förlopp i varje scenario: vad som förverkligas, vad som försenas och varför.',
    },
  },
  views: { label: 'Vy', work: 'Arbete', map: 'Karta' },
  panel: { show: 'Visa: {{name}}', hide: 'Dölj: {{name}}', kipupisteet: 'Problem' },
  lensChip: 'Lins: {{name}}',
};
